import { yupResolver } from "@hookform/resolvers/yup";
import useIsMounted from "ismounted";

import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Controller,
  FormProvider,
  useForm,
  useFormContext,
} from "react-hook-form";
import { reach } from "yup";
import { Country } from "../../../../components/forms/country";
import { Toggle } from "../../../../components/forms/toggle";
import { Grid } from "../../../../components/layout";
import { StepperContext } from "../../../../components/stepper/context";
import { countries } from "../../../../store/common/models";
import { LaunchConfigurationsDTO } from "../../../../store/launch-configurations/models/dto";
import { Container, FieldSection, SectionTitle } from "../common/components";
import { useDebouncedFormChange } from "../hooks";
import { InnerFormType } from "./common";
import { ConsentNote } from "./consent-note";
import { WhitelistedIPs } from "./whitelisted-ip";

export const SecurityAndRestrictions: React.FunctionComponent<{}> = () => {
  const { setValidation } = useContext(StepperContext);
  const isMounted = useIsMounted();

  const schema = LaunchConfigurationsDTO.Requests.SetSingle.useSchema();
  const resolver = useMemo(
    () =>
      reach(schema, "configuration").pick([
        "consentNote",
        "faceAttachmentRequired",
        "allowMultipleSubmissions",
        "prohibitedCountries",
        "poaRequiredCountries",
        "whitelistedIPs",
      ]),
    [schema]
  );

  const {
    setValue,
    getValues,
  } = useFormContext<LaunchConfigurationsDTO.Requests.SetSingle.T>();

  const innerForm = useForm<InnerFormType>({
    resolver: yupResolver(resolver),
    defaultValues: useMemo(
      () => ({
        ...getValues().configuration,
        askForConsentNote: !!getValues().configuration.consentNote,
      }),
      [getValues]
    ),
  });

  const setOuterValues = useCallback(
    (d) => {
      setValue(
        "configuration.consentNote",
        d.askForConsentNote ? d.consentNote : null
      );
      setValue(
        "configuration.faceAttachmentRequired",
        d.faceAttachmentRequired
      );
      setValue(
        "configuration.allowMultipleSubmissions",
        d.allowMultipleSubmissions
      );
      setValue("configuration.prohibitedCountries", d.prohibitedCountries);
      setValue("configuration.poaRequiredCountries", d.poaRequiredCountries);
      setValue("configuration.whitelistedIPs", d.whitelistedIPs);
    },
    [setValue]
  );

  useDebouncedFormChange(innerForm, setOuterValues);

  /** Set up the validation for the form */
  useEffect(() => {
    setValidation(() => () =>
      new Promise<void>((res, rej) => {
        innerForm.handleSubmit((d) => {
          setOuterValues(d);
          res();
        })();
      })
    );
    return () => {
      if (isMounted.current) {
        // We have to check if it's mounted otherwise it will clear current validation
        // This is because the prior page's clear validation is run after the new page's setValidation
        setValidation(() => () => Promise.resolve());
      }
    };
  }, [
    setValidation,
    innerForm.handleSubmit,
    isMounted.current,
    setOuterValues,
  ]);

  const [lastStoredCountries, setLastStoredCountries] = useState<
    Array<typeof countries.T>
  >([]);

  return (
    <Container>
      <FormProvider {...innerForm}>
        <FieldSection>
          <ConsentNote />
        </FieldSection>
        <FieldSection>
          <SectionTitle>Allow Multiple Submission</SectionTitle>
          <small>
            <em>
              This allows the same user user to submit the form multiple times
            </em>
          </small>
          <br />
          <Controller
            control={innerForm.control}
            name="allowMultipleSubmissions"
            defaultValue={false}
            render={({ field, fieldState: { error } }) => (
              <Toggle
                {...field}
                error={!!error}
                helperText={error?.message}
                label=""
              />
            )}
          />
        </FieldSection>
        <FieldSection>
          <SectionTitle>Prohibited Countries</SectionTitle>
          <small>
            <em>The list of countries from which users cannot participate</em>
          </small>
          <br />
          <Controller
            control={innerForm.control}
            name="prohibitedCountries"
            defaultValue={[]}
            render={({ field }) => <Country {...field} multiple />}
          />
        </FieldSection>
        <FieldSection>
          <SectionTitle>Proof of Address</SectionTitle>
          <small>
            <em>
              List of countries for which the user will need to provide proof of
              address
            </em>
          </small>
          <Controller
            control={innerForm.control}
            name="poaRequiredCountries"
            defaultValue={[]}
            render={({ field }) => (
              <Grid col-xs={2} alignItems="center">
                <strong>All countries</strong>
                <Toggle
                  onChange={(val: boolean) => {
                    if (val) {
                      setLastStoredCountries(
                        field.value.filter((x) => x !== "ALL") as Array<
                          typeof countries.T
                        >
                      );
                      field.onChange(["ALL"]);
                    } else {
                      field.onChange(lastStoredCountries);
                    }
                  }}
                  value={field.value.includes("ALL")}
                />
                <strong>Or specific countries</strong>
                <Country
                  {...field}
                  value={
                    field.value.includes("ALL")
                      ? []
                      : (field.value as Array<typeof countries.T>)
                  }
                  multiple
                  disabled={field.value.includes("ALL")}
                />
              </Grid>
            )}
          />
        </FieldSection>
        <FieldSection>
          <WhitelistedIPs />
        </FieldSection>
      </FormProvider>
    </Container>
  );
};
